import React from 'react';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import {
	GRADIENT_RAINBOW,
	GRADIENT_RAINBOW_FADED,
} from '../../../styles/tokens';

/**
 * PatternLibraryTextLink
 * A clean, elegant text prompt encouraging users to visit the Pattern Library.
 * Place this above your image browse buttons.
 *
 * Props:
 * - href: string (required) — destination URL for the Pattern Library
 * - variant: "discover" | "ideas" | "noProblem" | "perfect" | "readyMade"
 * - align: "left" | "center" | "right" (default "center")
 * - sx: MUI sx overrides
 * - onLinkClick: optional link click handler
 */
export default function PatternLibraryTextLink({
	href,
	variant = 'discover',
	align = 'center',
	sx,
	onLinkClick,
}) {
	const copy = {
		discover: {
			pre: "Don't have an image yet?",
			post: 'Discover inspiration in our',
		},
		ideas: {
			pre: 'Need ideas?',
			post: 'Explore beautiful designs in our',
		},
		noProblem: {
			pre: 'No image? No problem —',
			post: 'browse stunning designs in our',
		},
		perfect: {
			pre: 'Looking for the perfect image?',
			post: 'Find it in our',
		},
		readyMade: {
			pre: 'Want ready-made art?',
			post: 'Visit our',
		},
	}[variant];

	return (
		<Typography
			variant="h5"
			align={align}
			sx={{
				opacity: 0.9,
				mt: -1,
				mb: 3,
				px: 2,
				lineHeight: 1.4,
				fontSize: { xs: '1.3rem', md: '1.5rem' },
				...sx,
			}}
		>
			{copy.post}{' '}
			<Link
				href={href}
				onClick={onLinkClick}
				underline="none"
				sx={{
					fontSize: { xs: '1.4rem', md: '1.6rem' },
					fontWeight: 700,
					background: GRADIENT_RAINBOW,
					WebkitBackgroundClip: 'text',
					WebkitTextFillColor: 'transparent',
					textDecoration: 'none',
					position: 'relative',
					display: 'inline-block',
					'&::after': {
						content: '""',
						position: 'absolute',
						left: 0,
						right: 0,
						bottom: 3,
						height: 3,
						borderRadius: 2,
						background: GRADIENT_RAINBOW_FADED,
						transition: 'transform 200ms ease',
						transform: 'scaleX(0)',
						transformOrigin: 'left',
					},
					'&:hover::after': {
						transform: 'scaleX(1)',
					},
				}}
			>
				Pattern Library
			</Link>
			.
		</Typography>
	);
}
